import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../auth/AuthContext';
import PublicWhatsAppSupportButton from './PublicWhatsAppSupportButton.jsx';

export default function ResetPasswordView({ onBackToLogin, whatsappSupport }) {
  const auth = useAuth();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const passwordChecks = [
    { key: 'length', label: 'Pelo menos 8 caracteres', met: form.password.length >= 8 },
    { key: 'uppercase', label: 'Pelo menos 1 letra maiúscula', met: /[A-Z]/.test(form.password) },
    { key: 'lowercase', label: 'Pelo menos 1 letra minúscula', met: /[a-z]/.test(form.password) },
    { key: 'numbers', label: 'Pelo menos 1 número', met: /[0-9]/.test(form.password) },
    { key: 'special', label: 'Pelo menos 1 caractere especial', met: /[!@#$%^&*(),.?":{}|<>]/.test(form.password) },
    { key: 'match', label: 'As senhas coincidem', met: !!form.confirm && form.password === form.confirm }
  ];
  const passwordRulesPassed = passwordChecks.filter((check) => check.key !== 'match').every((check) => check.met);
  const passwordsMatch = passwordChecks.find((check) => check.key === 'match')?.met || false;

  const humanizeSupabasePasswordError = (err) => {
    const raw = String(err?.message || err?.error_description || err?.error || '').trim();
    const msg = raw.toLowerCase();
    if (!msg) return 'Falha ao atualizar senha.';

    if (msg.includes('password is known to be weak') || msg.includes('easy to guess')) {
      return 'Senha muito fraca ou comum. Escolha uma senha mais forte (misture letras maiusculas/minusculas, numeros e caractere especial).';
    }
    if (msg.includes('new password should be different') || msg.includes('password should be different')) {
      return 'A nova senha precisa ser diferente da senha atual.';
    }
    if (msg.includes('password should be at least') || msg.includes('minimum password length')) {
      return 'Senha muito curta. Use pelo menos 8 caracteres.';
    }
    if (msg.includes('invalid') && msg.includes('token')) {
      return 'Link de redefinicao invalido ou expirado. Solicite um novo e-mail de redefinicao.';
    }
    if (msg.includes('expired')) {
      return 'Link de redefinicao expirado. Solicite um novo e-mail de redefinicao.';
    }

    // fallback: remove ingles cru
    return raw.length > 180 ? 'Falha ao atualizar senha. Tente novamente.' : raw;
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;

    const parseTokens = () => {
      const hash = String(window.location.hash || '');
      const search = String(window.location.search || '');
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
      const type = params.get('type') || q.get('type');
      const access_token = params.get('access_token') || q.get('access_token');
      const refresh_token = params.get('refresh_token') || q.get('refresh_token');
      const code = q.get('code') || params.get('code');
      const token = q.get('token') || params.get('token');
      const token_hash = q.get('token_hash') || params.get('token_hash');
      const email = q.get('email') || params.get('email');
      const error_description = params.get('error_description') || q.get('error_description');
      return { type, access_token, refresh_token, code, token, token_hash, email, error_description };
    };

    const cleanUrl = () => {
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        ['access_token', 'refresh_token', 'type', 'code', 'token', 'token_hash', 'email', 'error_description'].forEach((k) => url.searchParams.delete(k));
        window.history.replaceState({}, document.title, url.toString());
      } catch {}
    };

    const handleUrlChange = async () => {
      try {
        const tokens = parseTokens();
        if (cancelled) return;
        if (tokens.error_description) {
          setNotice({ type: 'error', text: 'Link de redefinição inválido ou expirado. Solicite um novo e-mail.' });
          return;
        }
        if (tokens.type === 'recovery' || tokens.code || tokens.token || tokens.token_hash || (tokens.access_token && tokens.refresh_token)) {
          setCanEdit(true);
        }

        // Link no formato "implicit": tokens direto na URL (funciona em qualquer navegador).
        if (tokens.access_token && tokens.refresh_token) {
          const { error } = await supabase.auth.setSession({ access_token: tokens.access_token, refresh_token: tokens.refresh_token });
          if (!cancelled && !error) cleanUrl();
          return;
        }

        if (tokens.code) {
          const { error } = await supabase.auth.exchangeCodeForSession(tokens.code);
          if (!cancelled && !error) cleanUrl();
        }
        if (tokens.token_hash && (tokens.type === 'recovery' || !tokens.type)) {
          setCanEdit(true);
          const { error } = await supabase.auth.verifyOtp({
            type: 'recovery',
            token_hash: tokens.token_hash,
          });
          if (!cancelled && !error) cleanUrl();
          return;
        }
        // Fallback: se receber ?token=...&type=recovery, usa verifyOtp com e-mail
        if (tokens.token && (tokens.type === 'recovery' || !tokens.type)) {
          setCanEdit(true);
          const payload = tokens.email ? { type: 'recovery', token: tokens.token, email: tokens.email } : { type: 'recovery', token: tokens.token };
          const { error } = await supabase.auth.verifyOtp(payload);
          if (!cancelled && !error) cleanUrl();
        }
      } catch {}
    };

    // Tenta aplicar sessão existente rápido
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data?.session) setCanEdit(true);
    });

    // Executa imediatamente e após pequenos atrasos para pegar alterações assíncronas
    handleUrlChange();
    const t1 = setTimeout(handleUrlChange, 50);
    const t2 = setTimeout(handleUrlChange, 200);

    window.addEventListener('hashchange', handleUrlChange);
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      cancelled = true;
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener('hashchange', handleUrlChange);
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  const hasProof = (() => {
    try {
      if (typeof window === 'undefined') return false;
      const h = new URLSearchParams(String(window.location.hash || '').replace(/^#/, ''));
      const q = new URLSearchParams(String(window.location.search || '').replace(/^\?/, ''));
      return Boolean(
        q.get('code') ||
        q.get('token') ||
        q.get('token_hash') ||
        (h.get('access_token') && h.get('refresh_token')) ||
        h.get('code')
      );
    } catch { return false; }
  })();

  const canInteract = Boolean(auth.isAuthenticated || canEdit || hasProof);

  const canSubmit = useMemo(() => {
    if (!canInteract) return false;
    const p = String(form.password || '');
    const c = String(form.confirm || '');
    if (!p || !c) return false;
    if (!passwordRulesPassed) return false;
    if (!passwordsMatch) return false;
    return !loading;
  }, [canInteract, form.password, form.confirm, loading, passwordRulesPassed, passwordsMatch]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setNotice(null);
    const p = String(form.password || '');
    const c = String(form.confirm || '');
    if (!canInteract) {
      return setNotice({ type: 'error', text: 'Abra o link de redefinição recebido por e-mail para habilitar esta tela.' });
    }
    if (!p) return setNotice({ type: 'error', text: 'Informe a nova senha.' });
    if (!passwordRulesPassed) return setNotice({ type: 'error', text: 'A nova senha ainda não cumpre todos os requisitos.' });
    if (!passwordsMatch) return setNotice({ type: 'error', text: 'As senhas não conferem.' });

    setLoading(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session) {
        throw new Error('Abra o link de redefinição recebido por e-mail. Esta página sozinha não autoriza a troca de senha.');
      }
      const { error } = await supabase.auth.updateUser({ password: p });
      if (error) throw error;
      try { await supabase.auth.signOut(); } catch {}
      setForm({ password: '', confirm: '' });
      setNotice({ type: 'success', text: 'Senha atualizada com sucesso. Faça login novamente.' });
    } catch (err) {
      setNotice({ type: 'error', text: humanizeSupabasePasswordError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-slate-900/70 border border-slate-800 rounded-2xl shadow-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400">
              <KeyRound size={20} />
            </div>
            <div className="flex flex-col">
              <h1 className="text-xl font-black text-white leading-tight">Redefinir senha</h1>
              <p className="text-sm text-slate-400">Escolha uma nova senha para sua conta.</p>
            </div>
          </div>

          {!canInteract && (
            <div className="mb-4 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-sm text-slate-300">
              Abra o link de redefinição recebido por e-mail para habilitar a troca de senha.
            </div>
          )}

          {notice?.text && (
            <div className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
              notice.type === 'success'
                ? 'border-teal-500/30 bg-teal-500/10 text-teal-200'
                : 'border-rose-500/30 bg-rose-500/10 text-rose-200'
            }`}>
              {notice.text}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Nova senha</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-slate-800 text-slate-100 placeholder:text-slate-600 outline-none transition focus:border-orange-500 pr-12"
                  placeholder="********"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/60 p-3 space-y-2">
                {passwordChecks.map((check) => (
                  <div key={check.key} className="flex items-center gap-2 text-xs">
                    {check.met ? (
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    )}
                    <span className={check.met ? 'text-green-300' : 'text-slate-400'}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Confirmar senha</label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={form.confirm}
                  onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-slate-800 text-slate-100 placeholder:text-slate-600 outline-none transition focus:border-orange-500 pr-12"
                  placeholder="********"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full h-11 rounded-xl bg-orange-500 text-white font-black hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : 'Salvar nova senha'}
            </button>

            <button
              type="button"
              onClick={onBackToLogin}
              className="w-full h-11 rounded-xl bg-slate-800/70 hover:bg-slate-800 border border-slate-700 text-slate-200 font-black transition"
            >
              Voltar ao login
            </button>
          </form>
        </div>
      </div>
      <PublicWhatsAppSupportButton
        whatsappNumber={whatsappSupport?.number}
        enabled={Boolean(whatsappSupport?.enabled)}
        message="Olá! Preciso de suporte com a redefinição de senha na plataforma SOFERA."
      />
    </>
  );
}
